import type { ResolvedInstallationOrder } from "../types/installation";

const round2 = (n: number) => Math.round(Number(n) * 100) / 100;

/** Discount code + amount for voucher UI and cost breakdown. */
export function getResolvedDiscountDisplay(
  resolved: ResolvedInstallationOrder | null | undefined,
  requestedCode?: string
): { code: string; amount: number } {
  const requested = String(requestedCode ?? "").trim();
  if (!resolved) {
    return { code: requested, amount: 0 };
  }

  const lineItems = Array.isArray(resolved.line_items) ? resolved.line_items : [];
  const lineDiscountTotal = round2(
    lineItems.reduce(
      (s, li) =>
        s +
        Number(
          li.discount_on_total_quantity ??
            (li as { discount?: number }).discount ??
            0
        ),
      0
    )
  );

  const raw = resolved as ResolvedInstallationOrder & {
    discount?: number;
    job_discount_amount?: number;
  };

  let amount = round2(
    Number(raw.discount_amount ?? raw.discount ?? raw.job_discount_amount ?? 0)
  );
  if (amount <= 0 && lineDiscountTotal > 0) {
    amount = lineDiscountTotal;
  }

  const code =
    String(resolved.discount_code ?? "").trim() ||
    requested ||
    String(lineItems.find((li) => li.discount_code)?.discount_code ?? "").trim();

  return { code, amount };
}

export type InstallationOrderDiscountSource = {
  discount_code?: string;
  coupon_code?: string;
  discount_amount?: number;
  discount?: number;
  product_discount_amount?: number;
  discount_applies_to?: string;
  line_items?: Array<Record<string, unknown>>;
  order_charges?: Array<Record<string, unknown>>;
};

/** Cost breakdown discount — when user clears coupon, ignore stale order/resolve values. */
export function getInstallationCostBreakdownDiscount(args: {
  formikCode?: string;
  formikAmount?: number;
  formikDiscount?: number;
  formikProductDiscountAmount?: number;
  formikDiscountAppliesTo?: string;
  order?: InstallationOrderDiscountSource | null;
  preferFormikOnly?: boolean;
}): { code: string; amount: number } {
  if (args.preferFormikOnly) {
    const code = String(args.formikCode ?? "").trim();
    const appliesTo = String(args.formikDiscountAppliesTo ?? "installation").toLowerCase();
    const productDisc = Number(args.formikProductDiscountAmount ?? 0);
    const installDisc =
      appliesTo === "product" ? 0 : Number(args.formikAmount ?? args.formikDiscount ?? 0);
    const productFromAmount = appliesTo === "product" ? Number(args.formikAmount ?? 0) : 0;
    return { code, amount: productDisc || productFromAmount || installDisc };
  }

  const order = args.order;
  const code = String(
    order?.discount_code ?? args.formikCode ?? order?.coupon_code ?? ""
  ).trim();

  const orderLevel = Number(
    order?.discount_amount ?? order?.discount ?? args.formikAmount ?? args.formikDiscount ?? 0
  );
  const productLevel = Number(order?.product_discount_amount ?? args.formikProductDiscountAmount ?? 0);
  const lineItems = Array.isArray(order?.line_items) ? (order?.line_items ?? []) : [];
  const lineItemsSum = lineItems.reduce((s, li) => {
    const d = Number(
      li?.discount ??
        li?.discount_amount ??
        li?.discount_on_total_quantity ??
        li?.discount_on_total_quantity_amount ??
        0
    );
    return s + d;
  }, 0);
  const orderCharges = Array.isArray(order?.order_charges) ? (order?.order_charges ?? []) : [];
  const orderChargesDiscount = Number(
    orderCharges[0]?.discount_amount ?? orderCharges[0]?.discount ?? 0
  );

  let amount = orderLevel || productLevel || lineItemsSum || orderChargesDiscount;
  const appliesTo = String(
    args.formikDiscountAppliesTo ?? order?.discount_applies_to ?? "installation"
  ).toLowerCase();
  const formikDisc = Number(args.formikAmount ?? 0);
  if (!(amount > 0) && formikDisc > 0 && (code || appliesTo === "product")) {
    amount = formikDisc;
  }

  return { code, amount };
}

/** Voucher UI + cost breakdown — avoids stale resolve data after user clears the code. */
export function getOrderDiscountFieldDisplay(args: {
  formikCode?: string;
  formikAmount?: number;
  couponInput?: string;
  appliedVoucher?: { code: string; amount: number } | null;
  resolved?: ResolvedInstallationOrder | null;
}): { code: string; amount: number } {
  if (args.appliedVoucher?.code && args.appliedVoucher.amount > 0) {
    return args.appliedVoucher;
  }

  const fromFormik = {
    code: String(args.formikCode || "").trim(),
    amount: Number(args.formikAmount || 0),
  };

  if (!fromFormik.code) {
    return { code: "", amount: 0 };
  }

  const fromResolved = getResolvedDiscountDisplay(
    args.resolved,
    fromFormik.code || args.couponInput
  );

  return {
    code: fromFormik.code || fromResolved.code,
    amount: fromFormik.amount > 0 ? fromFormik.amount : fromResolved.amount,
  };
}
